import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, TextField, LinearProgress, Chip, Divider, Alert,
  CircularProgress, Stack, Paper, InputAdornment, Autocomplete, Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Upload, Trash2, Eye, X, FileText, Image as ImageIcon,
  Calendar, HardDrive, CloudUpload, Info, Tag as TagIcon, Pencil, Check,
} from 'lucide-react';
import * as c from '../../../constants';
import { useTranslation } from '../../../language';
import ButtonsMain from '../../../components/Button/ButtonMain';
import CustomDialogSmall from '../../../components/Dialog/DiaCompSmall';

const DOCS_URL = `${c.API_URL}/api/user-documents/`;
const QUOTA_URL = `${c.API_URL}/api/user-documents/quota/`;
const TAGS_URL = `${c.API_URL}/api/user-documents/tags/`;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function UserLabreportsDashboard() {
  const theme = useTheme();
  const { t } = useTranslation();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quota, setQuota] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  // Tag filter (list page)
  const [allTags, setAllTags] = useState([]);
  const [activeTagSlug, setActiveTagSlug] = useState(null);

  // Tag management (rename/delete)
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [editingTagId, setEditingTagId] = useState(null);
  const [editingTagName, setEditingTagName] = useState('');
  const [tagActionError, setTagActionError] = useState(null);
  const [tagActionLoadingId, setTagActionLoadingId] = useState(null);
  const [deleteTagCandidate, setDeleteTagCandidate] = useState(null);

  // Upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDate, setUploadDate] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadTags, setUploadTags] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const dateInputRef = useRef(null);
  const uploadTagsRef = useRef(null);

  // Viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);

  // Delete state
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Tag edit state
  const [tagEditDoc, setTagEditDoc] = useState(null);
  const [tagEditOpen, setTagEditOpen] = useState(false);
  const [tagEditValues, setTagEditValues] = useState([]);
  const [tagEditSaving, setTagEditSaving] = useState(false);
  const [tagEditError, setTagEditError] = useState(null);
  const tagEditTagsRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(DOCS_URL, {
        params: activeTagSlug ? { tag: activeTagSlug } : {},
      });
      setDocuments(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('lab_err_load'));
    } finally {
      setLoading(false);
    }
  }, [t, activeTagSlug]);

  const fetchQuota = useCallback(async () => {
    try {
      const res = await axios.get(QUOTA_URL);
      setQuota(res.data);
    } catch {
      // non-critical
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const res = await axios.get(TAGS_URL);
      setAllTags(res.data);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    fetchQuota();
    fetchTags();
  }, [fetchQuota, fetchTags]);

  // ── Dropzone ──
  const onDrop = useCallback((accepted, rejected) => {
    setUploadFiles((prev) => [...prev, ...accepted]);
    if (rejected.length > 0) {
      setUploadError(t('lab_err_file_rejected'));
    }
  }, [t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'application/pdf': ['.pdf'],
    },
    multiple: true,
    maxSize: 10 * 1024 * 1024,
  });

  // ── Upload ──
  const handleUpload = async () => {
    if (!uploadTitle.trim() || !uploadDate || uploadFiles.length === 0) {
      setUploadError(t('lab_err_validation'));
      return;
    }
    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('title', uploadTitle.trim());
      formData.append('report_date', uploadDate);
      uploadFiles.forEach((f) => formData.append('files', f));
      const finalTags = uploadTagsRef.current?.getCommittedTags() ?? uploadTags;
      finalTags.forEach((tag) => formData.append('tags', tag));
      await axios.post(DOCS_URL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) =>
          setUploadProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      setUploadOpen(false);
      resetUploadForm();
      fetchDocuments();
      fetchQuota();
      fetchTags();
    } catch (err) {
      const detail = err.response?.data;
      setUploadError(
        typeof detail === 'string' ? detail : JSON.stringify(detail) || t('lab_err_upload')
      );
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadTitle('');
    setUploadDate('');
    setUploadFiles([]);
    setUploadTags([]);
    setUploadError(null);
    setUploadProgress(0);
  };

  const handleUploadClose = () => {
    if (uploading) return;
    setUploadOpen(false);
    resetUploadForm();
  };

  // ── View ──
  const handleView = async (doc) => {
    setViewerLoading(true);
    setViewerOpen(true);
    setViewerDoc(null);
    try {
      // Re-fetch for fresh signed URLs (they expire in 15 min)
      const res = await axios.get(`${DOCS_URL}${doc.id}/`);
      setViewerDoc(res.data);
    } catch {
      setViewerDoc(doc);
    } finally {
      setViewerLoading(false);
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await axios.delete(`${DOCS_URL}${deleteId}/`);
      setDeleteOpen(false);
      setDeleteId(null);
      fetchDocuments();
      fetchQuota();
    } catch (err) {
      setError(err.response?.data?.detail || t('lab_err_delete'));
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  // ── Tags ──
  const handleEditTags = (doc) => {
    setTagEditDoc(doc);
    setTagEditValues((doc.tags || []).map((tg) => tg.name));
    setTagEditError(null);
    setTagEditOpen(true);
  };

  const handleTagEditClose = () => {
    if (tagEditSaving) return;
    setTagEditOpen(false);
    setTagEditDoc(null);
  };

  const handleTagEditSave = async () => {
    setTagEditSaving(true);
    setTagEditError(null);
    if (!tagEditDoc) {
      setTagEditError(t('lab_err_tag_no_doc'));
      setTagEditSaving(false);
      return;
    }
    try {
      const formData = new FormData();
      const finalTags = tagEditTagsRef.current?.getCommittedTags() ?? tagEditValues;
      finalTags.forEach((tag) => formData.append('tags', tag));
      await axios.patch(`${DOCS_URL}${tagEditDoc.id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fetchDocuments();
      fetchTags();
      setTagEditOpen(false);
      setTagEditDoc(null);
    } catch (err) {
      setTagEditError(err.response?.data?.detail || t('lab_err_tag_save'));
    } finally {
      setTagEditSaving(false);
    }
  };

  // ── Tag management (rename/delete) ──
  const handleManageTagsClose = () => {
    setManageTagsOpen(false);
    setEditingTagId(null);
    setEditingTagName('');
    setTagActionError(null);
  };

  const startRenameTag = (tag) => {
    setEditingTagId(tag.id);
    setEditingTagName(tag.name);
    setTagActionError(null);
  };

  const cancelRenameTag = () => {
    setEditingTagId(null);
    setEditingTagName('');
  };

  const saveRenameTag = async (tag) => {
    const trimmed = editingTagName.trim();
    if (!trimmed || trimmed === tag.name) {
      cancelRenameTag();
      return;
    }
    setTagActionLoadingId(tag.id);
    setTagActionError(null);
    try {
      const res = await axios.patch(`${TAGS_URL}${tag.id}/`, { name: trimmed });
      if (activeTagSlug === tag.slug) {
        setActiveTagSlug(res.data.slug);
      }
      setEditingTagId(null);
      setEditingTagName('');
      fetchTags();
      fetchDocuments();
    } catch (err) {
      setTagActionError(err.response?.data?.name?.[0] || t('lab_err_tag_rename'));
    } finally {
      setTagActionLoadingId(null);
    }
  };

  const handleDeleteTag = async () => {
    if (!deleteTagCandidate) return;
    setTagActionLoadingId(deleteTagCandidate.id);
    setTagActionError(null);
    try {
      await axios.delete(`${TAGS_URL}${deleteTagCandidate.id}/`);
      if (activeTagSlug === deleteTagCandidate.slug) {
        setActiveTagSlug(null);
      }
      setDeleteTagCandidate(null);
      fetchTags();
      fetchDocuments();
    } catch {
      setTagActionError(t('lab_err_tag_delete'));
    } finally {
      setTagActionLoadingId(null);
    }
  };

  // ── Viewer helpers ──
  const getImageItems = (doc) =>
    (doc?.files || [])
      .filter((f) => f.file_type === 'image')
      .sort((a, b) => a.order - b.order)
      .map((f) => ({ original: f.file_url, thumbnail: f.file_url }));

  const getPdfFiles = (doc) =>
    (doc?.files || [])
      .filter((f) => f.file_type === 'pdf')
      .sort((a, b) => a.order - b.order);

  const quotaPercent = quota
    ? Math.min(100, Math.round((quota.used_bytes / quota.limit_bytes) * 100))
    : 0;

  return (
    <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 4 }, pt: { xs: 4, sm: 6 }, pb: 6 }}>

      {/* ── Header ── */}
      <Stack spacing={2} sx={{ mb: 3, alignItems: 'flex-start' }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="h6" fontWeight={700}>{t('lab_dash_title')}</Typography>
            <IconButton size="small" onClick={() => setInfoOpen(true)} sx={{ color: 'text.secondary' }}>
              <Info size={24} />
            </IconButton>
          </Stack>
          <Typography variant="body1" color="text.secondary">{t('lab_dash_subtitle')}</Typography>
        </Box>
        <ButtonsMain
          action={() => setUploadOpen(true)}
          name={t('lab_btn_upload_report')}
          startIcon={<Upload size={16} />}
          sx={{
            px: { xs: '24px', sm: '16px', lg: '10px' },
            fontSize: { lg: '0.75rem' },
          }}
        />
      </Stack>

      {/* ── Quota Bar ── */}
      {quota && (
        <Paper sx={{
          p: 2, mb: 3, borderRadius: 4,
          border: '1px solid',
          ...(theme.palette.mode === 'light'
            ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
            : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
                backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
        }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <HardDrive size={15} color={theme.palette.text.secondary} />
            <Typography variant="1" color="text.secondary">
              {t('lab_quota_storage')}{' '}
              {quota.used_mb.toFixed(1)} {t('lab_quota_mb_of')} {quota.limit_mb} {t('lab_quota_mb_used')}
            </Typography>
            <Typography variant="body1" color="text.disabled" sx={{ ml: 'auto !important' }}>
              {quota.remaining_mb.toFixed(1)} {t('lab_quota_mb_free')}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={quotaPercent}
            sx={{
              height: 6,
              borderRadius: 3,
              '& .MuiLinearProgress-bar': {
                bgcolor:
                  quotaPercent > 80
                    ? 'error.main'
                    : quotaPercent > 60
                    ? 'warning.main'
                    : 'success.main',
              },
            }}
          />
        </Paper>
      )}

      {/* ── Tag filter bar ── */}
      {allTags.length > 0 && (
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
            <Chip
              label={t('lab_filter_all')}
              size="small"
              variant={activeTagSlug === null ? 'filled' : 'outlined'}
              color={activeTagSlug === null ? 'primary' : 'default'}
              onClick={() => setActiveTagSlug(null)}
            />
            {allTags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                variant={activeTagSlug === tag.slug ? 'filled' : 'outlined'}
                color={activeTagSlug === tag.slug ? 'primary' : 'default'}
                onClick={() => setActiveTagSlug(activeTagSlug === tag.slug ? null : tag.slug)}
              />
            ))}
          </Stack>
          <Tooltip title={t('lab_manage_tags_btn')}>
            <IconButton size="small" onClick={() => setManageTagsOpen(true)} sx={{ flexShrink: 0 }}>
              <Pencil size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      )}

      {/* ── Global error ── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Loading ── */}
      {loading && (
        <Box display="flex" alignItems="center" gap={2} sx={{ my: 4 }}>
          <CircularProgress size={24} />
          <Typography color="text.secondary">{t('lab_loading')}</Typography>
        </Box>
      )}

      {/* ── Empty state ── */}
      {!loading && documents.length === 0 && (
        <Paper sx={{
          p: 6, textAlign: 'center', borderRadius: 4,
          border: '1px dashed',
          ...(theme.palette.mode === 'light'
            ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
            : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
                backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
        }}>
          <CloudUpload size={48} color={theme.palette.text.disabled} style={{ marginBottom: 12 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('lab_empty_title')}
          </Typography>
          <Typography variant="1" color="text.disabled">
            {t('lab_empty_body')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <ButtonsMain
              action={() => setUploadOpen(true)}
              name={t('lab_empty_btn')}
              startIcon={<Upload size={16} />}
            />
          </Box>
        </Paper>
      )}

      {/* ── Document List ── */}
      {!loading && documents.length > 0 && (
        <Paper sx={{
          borderRadius: 4, overflow: 'hidden',
          border: '1px solid',
          ...(theme.palette.mode === 'light'
            ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
            : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
                backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
        }}>
          {documents.map((doc, idx) => (
            <Box key={doc.id}>
              {idx > 0 && <Divider />}
              <DocumentRow
                doc={doc}
                idx={idx}
                onView={() => handleView(doc)}
                onDelete={() => {
                  setDeleteId(doc.id);
                  setDeleteOpen(true);
                }}
                onEditTags={() => handleEditTags(doc)}
                t={t}
              />
            </Box>
          ))}
        </Paper>
      )}

      {/* ─────────────────────────────────────────────
          Upload Dialog
      ───────────────────────────────────────────── */}
      <Dialog
        open={uploadOpen}
        onClose={handleUploadClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" fontWeight={700}>{t('lab_dia_upload_title')}</Typography>
          <IconButton onClick={handleUploadClose} disabled={uploading} size="small">
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label={t('lab_field_title_label')}
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              fullWidth
              required
              placeholder={t('lab_field_title_ph')}
              disabled={uploading}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label={t('lab_field_date_label')}
              type="date"
              value={uploadDate}
              onChange={(e) => setUploadDate(e.target.value)}
              fullWidth
              required
              disabled={uploading}
              InputLabelProps={{ shrink: true }}
              inputRef={dateInputRef}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      disabled={uploading}
                      onClick={() => dateInputRef.current?.showPicker?.()}
                    >
                      <Calendar size={18} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TagsAutocomplete
              ref={uploadTagsRef}
              value={uploadTags}
              onChange={setUploadTags}
              disabled={uploading}
              label={t('lab_field_tags_label')}
              placeholder={t('lab_field_tags_ph')}
              t={t}
            />

            {/* Drop zone */}
            <Box
              {...getRootProps()}
              sx={{
                border: `2px dashed ${
                  isDragActive ? theme.palette.primary.main : theme.palette.divider
                }`,
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                transition: 'border-color 0.2s, background-color 0.2s',
                '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
              }}
            >
              <input {...getInputProps()} />
              <CloudUpload
                size={32}
                color={theme.palette.text.secondary}
                style={{ marginBottom: 8 }}
              />
              <Typography variant="body1" color="text.secondary">
                {isDragActive ? t('lab_drop_active') : t('lab_drop_idle')}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {t('lab_drop_hint')}
              </Typography>
            </Box>

            {/* Selected files list */}
            {uploadFiles.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  {uploadFiles.length}{' '}
                  {uploadFiles.length !== 1 ? t('lab_files_ready_pl') : t('lab_files_ready')}
                </Typography>
                <Stack spacing={0.5}>
                  {uploadFiles.map((f, i) => (
                    <Stack
                      key={i}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ bgcolor: 'grey.C50', borderRadius: 1, px: 1.5, py: 0.75 }}
                    >
                      {f.type === 'application/pdf' ? (
                        <FileText size={14} />
                      ) : (
                        <ImageIcon size={14} />
                      )}
                      <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                        {f.name}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {(f.size / 1024).toFixed(0)} KB
                      </Typography>
                      <IconButton
                        size="small"
                        disabled={uploading}
                        onClick={() =>
                          setUploadFiles((prev) => prev.filter((_, idx) => idx !== i))
                        }
                      >
                        <X size={12} />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Upload progress */}
            {uploading && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('lab_uploading')} {uploadProgress}%
                </Typography>
              </Box>
            )}

            {uploadError && <Alert severity="error">{uploadError}</Alert>}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleUploadClose} disabled={uploading}>
            {t('lab_btn_cancel')}
          </Button>
          <ButtonsMain
            action={handleUpload}
            name={uploading ? t('lab_uploading') : t('lab_btn_upload_submit')}
            disabled={uploading || !uploadTitle.trim() || !uploadDate || uploadFiles.length === 0}
            startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <Upload size={14} />}
          />
        </DialogActions>
      </Dialog>

      {/* ─────────────────────────────────────────────
          Viewer Dialog
      ───────────────────────────────────────────── */}
      <Dialog
        open={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setViewerDoc(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, minHeight: '60vh' } }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {viewerDoc?.title || '…'}
            </Typography>
            {viewerDoc?.report_date && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                <Calendar size={13} color={theme.palette.text.secondary} />
                <Typography variant="caption" color="text.secondary">
                  {formatDate(viewerDoc.report_date)}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                  · {viewerDoc.file_count}{' '}
                  {viewerDoc.file_count !== 1 ? t('lab_chip_page_pl') : t('lab_chip_page')}
                  {' '}· {viewerDoc.total_size_display}
                </Typography>
              </Stack>
            )}
            {viewerDoc?.tags?.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                {viewerDoc.tags.map((tag) => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.6rem', height: 18 }}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <IconButton
            onClick={() => {
              setViewerOpen(false);
              setViewerDoc(null);
            }}
            size="small"
            sx={{ mt: 0.5 }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
          {viewerLoading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ minHeight: 300 }}
            >
              <CircularProgress />
            </Box>
          )}

          {!viewerLoading && viewerDoc && (
            <>
              {/* Image gallery */}
              {getImageItems(viewerDoc).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <ImageGallery
                    items={getImageItems(viewerDoc)}
                    showIndex
                    showFullscreenButton
                    showPlayButton={false}
                    lazyLoad
                  />
                </Box>
              )}

              {/* PDF pages */}
              {getPdfFiles(viewerDoc).length > 0 && (
                <Box>
                  {getImageItems(viewerDoc).length > 0 && <Divider sx={{ my: 2 }} />}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <FileText size={16} />
                    <Typography variant="subtitle2">
                      {getPdfFiles(viewerDoc).length > 1
                        ? t('lab_pdf_pages')
                        : t('lab_pdf_doc')}
                    </Typography>
                  </Stack>
                  <Stack spacing={2}>
                    {getPdfFiles(viewerDoc).map((f) => (
                      <Box
                        key={f.id}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
                      >
                        <iframe
                          src={f.file_url}
                          title={`${t('lab_pdf_page_aria')} ${f.order + 1}`}
                          style={{ width: '100%', height: 620, border: 'none', display: 'block' }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─────────────────────────────────────────────
          Delete Confirm Dialog
      ───────────────────────────────────────────── */}
      <Dialog
        open={deleteOpen}
        onClose={() => {
          if (!deleting) {
            setDeleteOpen(false);
            setDeleteId(null);
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>{t('lab_del_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            {t('lab_del_body')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setDeleteOpen(false);
              setDeleteId(null);
            }}
            disabled={deleting}
          >
            {t('lab_btn_cancel')}
          </Button>
          <ButtonsMain
            action={handleDelete}
            name={deleting ? t('lab_deleting') : t('lab_del_btn')}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <Trash2 size={14} />}
          />
        </DialogActions>
      </Dialog>

      {/* ─────────────────────────────────────────────
          Edit Tags Dialog
      ───────────────────────────────────────────── */}
      <Dialog
        open={tagEditOpen}
        onClose={handleTagEditClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" fontWeight={700}>{t('lab_tag_edit_title')}</Typography>
          <IconButton onClick={handleTagEditClose} disabled={tagEditSaving} size="small">
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TagsAutocomplete
              ref={tagEditTagsRef}
              value={tagEditValues}
              onChange={setTagEditValues}
              disabled={tagEditSaving}
              label={t('lab_field_tags_label')}
              placeholder={t('lab_field_tags_ph')}
              t={t}
            />
            {tagEditError && <Alert severity="error">{tagEditError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleTagEditClose} disabled={tagEditSaving}>
            {t('lab_btn_cancel')}
          </Button>
          <ButtonsMain
            action={handleTagEditSave}
            name={tagEditSaving ? t('lab_uploading') : t('lab_btn_tag_save')}
            disabled={tagEditSaving}
            startIcon={tagEditSaving ? <CircularProgress size={14} color="inherit" /> : <TagIcon size={14} />}
          />
        </DialogActions>
      </Dialog>

      {/* ─────────────────────────────────────────────
          Manage Tags Dialog
      ───────────────────────────────────────────── */}
      <Dialog
        open={manageTagsOpen}
        onClose={handleManageTagsClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" fontWeight={700}>{t('lab_manage_tags_title')}</Typography>
          <IconButton onClick={handleManageTagsClose} size="small">
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {allTags.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('lab_manage_tags_empty')}
              </Typography>
            )}
            {allTags.map((tag) => (
              <Stack key={tag.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
                {editingTagId === tag.id ? (
                  <>
                    <TextField
                      size="small"
                      value={editingTagName}
                      onChange={(e) => setEditingTagName(e.target.value)}
                      autoFocus
                      fullWidth
                      disabled={tagActionLoadingId === tag.id}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveRenameTag(tag);
                        if (e.key === 'Escape') cancelRenameTag();
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => saveRenameTag(tag)}
                      disabled={tagActionLoadingId === tag.id}
                    >
                      {tagActionLoadingId === tag.id ? <CircularProgress size={16} /> : <Check size={16} />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={cancelRenameTag}
                      disabled={tagActionLoadingId === tag.id}
                    >
                      <X size={16} />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap title={tag.name}>
                      {tag.name}
                    </Typography>
                    <Tooltip title={t('lab_tag_rename_btn')}>
                      <IconButton size="small" onClick={() => startRenameTag(tag)}>
                        <Pencil size={14} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('lab_del_btn')}>
                      <IconButton size="small" onClick={() => setDeleteTagCandidate(tag)} sx={{ color: 'error.main' }}>
                        <Trash2 size={14} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            ))}
            {tagActionError && <Alert severity="error" sx={{ mt: 1 }}>{tagActionError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleManageTagsClose}>{t('lab_btn_cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* ─────────────────────────────────────────────
          Delete Tag Confirm Dialog
      ───────────────────────────────────────────── */}
      <Dialog
        open={!!deleteTagCandidate}
        onClose={() => {
          if (tagActionLoadingId !== deleteTagCandidate?.id) setDeleteTagCandidate(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>{t('lab_tag_del_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
            {deleteTagCandidate?.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('lab_tag_del_body')}
          </Typography>
          {tagActionError && <Alert severity="error" sx={{ mt: 2 }}>{tagActionError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDeleteTagCandidate(null)}
            disabled={tagActionLoadingId === deleteTagCandidate?.id}
          >
            {t('lab_btn_cancel')}
          </Button>
          <ButtonsMain
            action={handleDeleteTag}
            name={tagActionLoadingId === deleteTagCandidate?.id ? t('lab_deleting') : t('lab_del_btn')}
            disabled={tagActionLoadingId === deleteTagCandidate?.id}
            startIcon={
              tagActionLoadingId === deleteTagCandidate?.id
                ? <CircularProgress size={14} color="inherit" />
                : <Trash2 size={14} />
            }
          />
        </DialogActions>
      </Dialog>

      {/* ── Info Dialog ── */}
      <CustomDialogSmall
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={t('lab_info_title')}
        content={
          <Box sx={{ px: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>{t('lab_info_p1')}</Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>{t('lab_info_p2_intro')}</Typography>
            <Box component="ol" sx={{ pl: 2.5, mb: 2, '& li': { mb: 0.5 } }}>
              <li><Typography variant="body1">{t('lab_info_p2_step1')}</Typography></li>
              <li><Typography variant="body1">{t('lab_info_p2_step2')}</Typography></li>
              <li><Typography variant="body1">{t('lab_info_p2_step3')}</Typography></li>
              <li><Typography variant="body1">{t('lab_info_p2_step4')}</Typography></li>
            </Box>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>{t('lab_info_p3')}</Typography>
            <Typography variant="body1" color="text.secondary">{t('lab_info_p4')}</Typography>
          </Box>
        }
      />
    </Box>
  );
}

// ─────────────────────────────────────────────
// Document Row (list view)
// ─────────────────────────────────────────────
function DocumentRow({ doc, idx, onView, onDelete, onEditTags, t }) {
  const theme = useTheme();

  const firstImage = doc.files?.find((f) => f.file_type === 'image');
  const pdfCount = doc.files?.filter((f) => f.file_type === 'pdf').length ?? 0;
  const imgCount = doc.files?.filter((f) => f.file_type === 'image').length ?? 0;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        px: 2,
        py: 1.5,
        borderLeft: `10px solid ${idx % 2 === 0 ? theme.palette.tertiary.C300 : theme.palette.quartiary.C300}`,
        transition: 'background-color 0.15s',
        '&:hover': {
          bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
        },
      }}
    >
      {/* Thumbnail / icon */}
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
          overflow: 'hidden',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {firstImage ? (
          <img
            src={firstImage.file_url}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <FileText size={20} color={theme.palette.text.disabled} />
        )}
      </Box>

      {/* Title + tags */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" fontWeight={600} noWrap title={doc.title}>
          {doc.title}
        </Typography>
        {doc.tags?.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
            {doc.tags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.6rem', height: 18 }}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* Date */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' }, minWidth: 110 }}>
        <Calendar size={16} color={idx % 2 === 0 ? theme.palette.tertiary.C300 : theme.palette.quartiary.C300} />
        <Typography variant="caption" fontWeight={600}>
          {formatDate(doc.report_date)}
        </Typography>
      </Stack>

      {/* Type + size chips */}
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, display: { xs: 'none', md: 'flex' } }}>
        {imgCount > 0 && (
          <Chip
            label={`${imgCount} ${imgCount !== 1 ? t('lab_chip_img_pl') : t('lab_chip_img')}`}
            size="small" variant="outlined"
            sx={{ fontSize: '0.6rem', height: 20 }}
          />
        )}
        {pdfCount > 0 && (
          <Chip
            label={`${pdfCount} ${pdfCount !== 1 ? t('lab_chip_pdf_pl') : t('lab_chip_pdf')}`}
            size="small" variant="outlined"
            sx={{ fontSize: '0.6rem', height: 20 }}
          />
        )}
        <Chip
          label={doc.total_size_display}
          size="small" variant="outlined"
          sx={{ fontSize: '0.6rem', height: 20 }}
        />
      </Stack>

      {/* Actions */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
        <ButtonsMain action={onView} name={t('lab_btn_view')} startIcon={<Eye size={14} />} />
        <Tooltip title={t('lab_btn_edit_tags')}>
          <IconButton size="small" onClick={onEditTags}>
            <TagIcon size={16} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onDelete} sx={{ color: 'error.main' }}>
          <Trash2 size={16} />
        </IconButton>
      </Stack>
    </Stack>
  );
}

// ─────────────────────────────────────────────
// Tags Autocomplete (freeSolo multi-select, backed by /tags/?q=)
// ─────────────────────────────────────────────
const TagsAutocomplete = forwardRef(function TagsAutocomplete(
  { value, onChange, disabled, label, placeholder, t },
  ref
) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const debounceRef = useRef(null);

  const fetchOptions = useCallback((query) => {
    setLoading(true);
    axios
      .get(TAGS_URL, { params: query ? { q: query } : {} })
      .then((res) => setOptions(res.data.map((tag) => tag.name)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchOptions('');
    return () => clearTimeout(debounceRef.current);
  }, [fetchOptions]);

  const handleInputChange = (_e, newInput, reason) => {
    setInputValue(newInput);
    if (reason !== 'input') return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(newInput), 250);
  };

  useImperativeHandle(ref, () => ({
    // Commits any text still sitting uncommitted in the input (typed but
    // never confirmed with Enter/selection) so a direct "Save" click
    // doesn't silently drop it. Returns the up-to-date tag list synchronously,
    // since the onChange-driven state update wouldn't be visible in the
    // caller's own closure until the next render.
    getCommittedTags: () => {
      const trimmed = inputValue.trim();
      if (trimmed && !value.includes(trimmed)) {
        const merged = [...value, trimmed];
        onChange(merged);
        setInputValue('');
        return merged;
      }
      return value;
    },
  }), [inputValue, value, onChange]);

  return (
    <Autocomplete
      multiple
      freeSolo
      options={options}
      value={value}
      disabled={disabled}
      loading={loading}
      onChange={(_e, newValue) => onChange(newValue)}
      onInputChange={handleInputChange}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} variant="outlined" size="small" label={option} {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={t('lab_tag_no_options')}
    />
  );
});
